import React, { useEffect, useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import HelpAssistant from '../common/HelpAssistant';
import { useUser } from '../../contexts/UserContext';
import { API_BASE_URL } from '../../config';
import './Layout.css';

const TENANT = 'choice';

/**
 * Fetches a tenant-content JSON section the same way ComponentRegistry does:
 *  1) DB-backed CMS  (instant edits via /admin/content)
 *  2) Static file in public/tenant-content/{tenant}/components/{key}.json
 */
const useTenantContent = (sectionKey) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // 1) Try DB
      try {
        const tenantId = (typeof window !== 'undefined' && window.__CHOICE_TENANT_ID) || TENANT;
        const res = await fetch(`${API_BASE_URL}/api/tenant-content/${tenantId}/${sectionKey}`);
        if (res.ok) {
          const json = await res.json();
          if (!cancelled && json?.content) {
            setData(json.content);
            return;
          }
        }
      } catch {
        /* fall through */
      }

      // 2) Fallback to static file
      try {
        const res = await fetch(`/tenant-content/${TENANT}/components/${sectionKey}.json`);
        if (res.ok) {
          const json = await res.json();
          if (!cancelled) setData(json);
        }
      } catch {
        /* leave data as null; Header/Footer have built-in fallbacks */
      }
    };

    load();
    return () => { cancelled = true; };
  }, [sectionKey]);

  return data;
};

const Layout = ({ children }) => {
  const { isAuthenticated } = useUser();
  const headerData = useTenantContent('header');
  const footerData = useTenantContent('footer');

  // Both authenticated and unauthenticated users see the same purple-gradient
  // Header (it switches its nav internally based on isAuthenticated). The
  // header is `position: fixed`, so the layout reserves top padding for it.
  const layoutClass = 'app-layout';

  return (
    <div className={layoutClass}>
      <Header headerData={headerData} />
      <main className="main-content">
        {children}
      </main>
      <Footer footerData={footerData} />
      {/* Global floating AI Help Assistant — appears on every Layout-wrapped page. */}
      <HelpAssistant />
    </div>
  );
};

export default Layout;
